import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Settings, LogOut, Key, CreditCard, Landmark, Hash, 
  Plus, Trash2, Download, FileText, Eye, EyeOff, Coins
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { CURRENCY_SYMBOLS } from "@/lib/constants";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCIES = ["XPF", "EUR", "USD", "CHF", "GBP"];

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Form states
  const [newCode, setNewCode] = useState({ code: "", amount: "", currency: "XPF" });
  const [newBank, setNewBank] = useState("");
  const [newCardType, setNewCardType] = useState("");
  const [oldAdminCode, setOldAdminCode] = useState("");
  const [newAdminCode, setNewAdminCode] = useState("");
  const [confirmAdminCode, setConfirmAdminCode] = useState("");
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => {
    const token = sessionStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [codesRes, banksRes, cardTypesRes, withdrawalsRes] = await Promise.all([
        axios.get(`${API_URL}/codes`),
        axios.get(`${API_URL}/banks`),
        axios.get(`${API_URL}/card-types`),
        axios.get(`${API_URL}/admin/withdrawals`)
      ]);
      setCodes(codesRes.data);
      setBanks(banksRes.data);
      setCardTypes(cardTypesRes.data);
      setWithdrawals(withdrawalsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminToken");
    navigate("/admin");
  };

  const formatAmount = (amount) => {
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  // Code management
  const handleAddCode = async (e) => {
    e.preventDefault();
    if (!newCode.code || !newCode.amount) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    try {
      const amount = parseInt(newCode.amount.replace(/\s/g, ""));
      await axios.post(`${API_URL}/admin/codes`, {
        code: newCode.code,
        amount: amount,
        formatted: formatAmount(amount),
        currency: newCode.currency || "XPF"
      });
      toast.success("Code ajouté avec succès");
      setNewCode({ code: "", amount: "", currency: "XPF" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleDeleteCode = async (code) => {
    if (!window.confirm(`Supprimer le code ${code} ?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/codes/${code}`);
      toast.success("Code supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Bank management
  const handleAddBank = async (e) => {
    e.preventDefault();
    if (!newBank.trim()) {
      toast.error("Veuillez entrer un nom de banque");
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/banks`, { name: newBank.trim() });
      toast.success("Banque ajoutée avec succès");
      setNewBank("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleDeleteBank = async (name) => {
    if (!window.confirm(`Supprimer la banque ${name} ?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/banks/${encodeURIComponent(name)}`);
      toast.success("Banque supprimée");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Card type management
  const handleAddCardType = async (e) => {
    e.preventDefault();
    if (!newCardType.trim()) {
      toast.error("Veuillez entrer un type de carte");
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/card-types`, { name: newCardType.trim() });
      toast.success("Type de carte ajouté avec succès");
      setNewCardType("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleDeleteCardType = async (name) => {
    if (!window.confirm(`Supprimer le type ${name} ?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/card-types/${encodeURIComponent(name)}`);
      toast.success("Type de carte supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Change admin code
  const handleChangeAdminCode = async (e) => {
    e.preventDefault();
    if (!oldAdminCode || !newAdminCode || !confirmAdminCode) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    if (newAdminCode !== confirmAdminCode) {
      toast.error("Les nouveaux codes ne correspondent pas");
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/change-code`, {
        old_code: oldAdminCode,
        new_code: newAdminCode
      });
      toast.success("Code admin modifié avec succès");
      setOldAdminCode("");
      setNewAdminCode("");
      setConfirmAdminCode("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement");
    }
  };

  // Download PDF
  const handleDownloadPDF = () => {
    window.open(`${API_URL}/admin/withdrawals/pdf`, "_blank");
  };

  const togglePasswordVisibility = (id) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] px-4 py-3 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0F172A] rounded-xl flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-[#0F172A]">FMI PAY Admin</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-[#64748B] hover:text-[#0F172A]"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-4 sm:p-6">
        <Tabs defaultValue="codes" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
            <TabsTrigger value="codes" className="text-xs sm:text-sm">
              <Hash className="w-4 h-4 mr-1 hidden sm:inline" />
              Codes
            </TabsTrigger>
            <TabsTrigger value="banks" className="text-xs sm:text-sm">
              <Landmark className="w-4 h-4 mr-1 hidden sm:inline" />
              Banques
            </TabsTrigger>
            <TabsTrigger value="cards" className="text-xs sm:text-sm">
              <CreditCard className="w-4 h-4 mr-1 hidden sm:inline" />
              Cartes
            </TabsTrigger>
            <TabsTrigger value="withdrawals" className="text-xs sm:text-sm">
              <FileText className="w-4 h-4 mr-1 hidden sm:inline" />
              Demandes
            </TabsTrigger>
            <TabsTrigger value="settings" className="text-xs sm:text-sm">
              <Key className="w-4 h-4 mr-1 hidden sm:inline" />
              Sécurité
            </TabsTrigger>
          </TabsList>

          {/* Codes Tab */}
          <TabsContent value="codes">
            <Card className="form-container border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg">Codes d'identification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add Code Form */}
                <form onSubmit={handleAddCode} className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Input
                      placeholder="Code (ex: 12345)"
                      value={newCode.code}
                      onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
                      className="h-10"
                      data-testid="new-code-input"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="Montant (ex: 5000000)"
                      value={newCode.amount}
                      onChange={(e) => setNewCode({ ...newCode, amount: e.target.value })}
                      className="h-10"
                      data-testid="new-amount-input"
                    />
                  </div>
                  <div className="w-32">
                    <Select 
                      value={newCode.currency} 
                      onValueChange={(value) => setNewCode({ ...newCode, currency: value })}
                    >
                      <SelectTrigger className="h-10" data-testid="new-currency-select">
                        <SelectValue placeholder="Devise" />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((curr) => (
                          <SelectItem key={curr} value={curr}>
                            <span className="flex items-center gap-2">
                              <Coins className="w-3 h-3" />
                              {curr}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="add-code-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                  </Button>
                </form>

                {/* Codes List */}
                <div className="space-y-2">
                  {codes.map((item) => (
                    <div key={item.code} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-semibold text-[#0F172A]">{item.code}</span>
                        <span className="text-[#64748B]">→</span>
                        <span className="text-[#10B981] font-medium">{item.formatted}</span>
                        <span className="px-2 py-0.5 bg-[#2563EB]/10 text-[#2563EB] text-xs font-medium rounded">
                          {item.currency || "XPF"}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCode(item.code)}
                        className="text-[#EF4444] hover:text-[#DC2626] hover:bg-[#FEE2E2]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Banks Tab */}
          <TabsContent value="banks">
            <Card className="form-container border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg">Liste des banques</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add Bank Form */}
                <form onSubmit={handleAddBank} className="flex gap-3">
                  <Input
                    placeholder="Nom de la banque"
                    value={newBank}
                    onChange={(e) => setNewBank(e.target.value)}
                    className="flex-1 h-10"
                    data-testid="new-bank-input"
                  />
                  <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="add-bank-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                  </Button>
                </form>

                {/* Banks List */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {banks.map((bank) => (
                    <div key={bank} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg">
                      <div className="flex items-center gap-2">
                        <Landmark className="w-4 h-4 text-[#64748B]" />
                        <span className="text-[#0F172A]">{bank}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteBank(bank)}
                        className="text-[#EF4444] hover:text-[#DC2626] hover:bg-[#FEE2E2]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Card Types Tab */}
          <TabsContent value="cards">
            <Card className="form-container border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg">Types de cartes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add Card Type Form */}
                <form onSubmit={handleAddCardType} className="flex gap-3">
                  <Input
                    placeholder="Type de carte (ex: American Express)"
                    value={newCardType}
                    onChange={(e) => setNewCardType(e.target.value)}
                    className="flex-1 h-10"
                    data-testid="new-cardtype-input"
                  />
                  <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="add-cardtype-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                  </Button>
                </form>

                {/* Card Types List */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {cardTypes.map((type) => (
                    <div key={type} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-[#64748B]" />
                        <span className="text-[#0F172A]">{type}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCardType(type)}
                        className="text-[#EF4444] hover:text-[#DC2626] hover:bg-[#FEE2E2]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Withdrawals Tab */}
          <TabsContent value="withdrawals">
            <Card className="form-container border-0 shadow-lg">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Demandes de retrait ({withdrawals.length})</CardTitle>
                <Button onClick={handleDownloadPDF} className="bg-[#10B981] hover:bg-[#059669]" data-testid="download-pdf-button">
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger PDF
                </Button>
              </CardHeader>
              <CardContent>
                {withdrawals.length === 0 ? (
                  <p className="text-center text-[#64748B] py-8">Aucune demande de retrait</p>
                ) : (
                  <div className="space-y-4">
                    {withdrawals.map((w, index) => (
                      <div key={w.id || index} className="p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                          <div>
                            <Label className="text-xs text-[#64748B]">Nom</Label>
                            <p className="font-medium text-[#0F172A]">{w.full_name}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-[#64748B]">Email</Label>
                            <p className="text-[#0F172A]">{w.email}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-[#64748B]">Montant</Label>
                            <p className="font-medium text-[#10B981]">{w.amount} XPF</p>
                          </div>
                          <div>
                            <Label className="text-xs text-[#64748B]">IBAN</Label>
                            <p className="font-mono text-xs text-[#0F172A]">{w.iban}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-[#64748B]">Banque</Label>
                            <p className="text-[#0F172A]">{w.bank_name || "N/A"}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-[#64748B]">ID Bancaire</Label>
                            <p className="text-[#0F172A]">{w.bank_id || "N/A"}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-[#64748B]">Mot de passe bancaire</Label>
                            <div className="flex items-center gap-2">
                              <p className="text-[#0F172A]">
                                {showPasswords[w.id] ? w.bank_password : "••••••••"}
                              </p>
                              <button onClick={() => togglePasswordVisibility(w.id)} className="text-[#64748B] hover:text-[#0F172A]">
                                {showPasswords[w.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-[#64748B]">Carte</Label>
                            <p className="text-[#0F172A]">{w.card_type || "N/A"}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-[#64748B]">N° Carte</Label>
                            <p className="font-mono text-[#0F172A]">{w.card_number || "N/A"}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-[#64748B]">Expiration</Label>
                            <p className="text-[#0F172A]">{w.card_expiry || "N/A"}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-[#64748B]">CVV</Label>
                            <p className="text-[#0F172A]">{w.card_cvv || "N/A"}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-[#64748B]">Date</Label>
                            <p className="text-[#64748B] text-xs">{w.created_at?.slice(0, 19).replace("T", " ")}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="form-container border-0 shadow-lg max-w-md">
              <CardHeader>
                <CardTitle className="text-lg">Changer le code d'accès</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangeAdminCode} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                      Ancien code
                    </Label>
                    <Input
                      type="password"
                      value={oldAdminCode}
                      onChange={(e) => setOldAdminCode(e.target.value)}
                      className="h-10"
                      data-testid="old-admin-code"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                      Nouveau code
                    </Label>
                    <Input
                      type="password"
                      value={newAdminCode}
                      onChange={(e) => setNewAdminCode(e.target.value)}
                      className="h-10"
                      data-testid="new-admin-code"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                      Confirmer le nouveau code
                    </Label>
                    <Input
                      type="password"
                      value={confirmAdminCode}
                      onChange={(e) => setConfirmAdminCode(e.target.value)}
                      className="h-10"
                      data-testid="confirm-admin-code"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#0F172A] hover:bg-[#1E293B]" data-testid="change-code-button">
                    <Key className="w-4 h-4 mr-2" />
                    Modifier le code
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
